import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Loader2, Shield, ShieldCheck, ShieldOff, Key, Mail, Smartphone, Plus, Trash2, GripVertical, Crown } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useUnifiedMFAStatus, useUnenrollMFAMethod, useUpdateMFAMethodOrder, type MfaMethod, type MfaMethodType } from '@/hooks/useUnifiedMFA';
import { MFAEnrollment } from '@/components/auth/MFAEnrollment';
import { EmailMfaEnrollment } from '@/components/auth/EmailMfaEnrollment';
import { SmsMfaEnrollment } from '@/components/auth/SmsMfaEnrollment';
import { MfaMethodSelector, type MfaMethod as SelectorMfaMethod } from '@/components/auth/MfaMethodSelector';

interface UnifiedMfaSectionProps {
  userId: string;
}

function getMethodIcon(type: MfaMethodType) {
  switch (type) {
    case 'totp':
      return <Key className="h-5 w-5" />;
    case 'email':
      return <Mail className="h-5 w-5" />;
    case 'sms':
      return <Smartphone className="h-5 w-5" />;
  }
}

interface SortableMfaMethodCardProps {
  method: MfaMethod;
  isPrimary: boolean;
  showDragHandle: boolean;
  getMethodLabel: (type: MfaMethodType) => string;
  onRemove: (method: MfaMethod) => void;
  isRemoving: boolean;
  primaryLabel: string;
}

function SortableMfaMethodCard({
  method,
  isPrimary,
  showDragHandle,
  getMethodLabel,
  onRemove,
  isRemoving,
  primaryLabel,
}: SortableMfaMethodCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: method.type });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-muted/50 rounded-lg ${isDragging ? 'shadow-lg ring-2 ring-primary' : ''}`}
    >
      <div className="flex items-center gap-3">
        {showDragHandle && (
          <div
            className="cursor-grab active:cursor-grabbing"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center">
          {getMethodIcon(method.type)}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">{getMethodLabel(method.type)}</p>
            {isPrimary && (
              <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                <Crown className="h-3 w-3" />
                {primaryLabel}
              </span>
            )}
          </div>
          {method.destination && (
            <p className="text-sm text-muted-foreground">{method.destination}</p>
          )}
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRemove(method)}
        disabled={isRemoving}
        className="text-destructive hover:text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function UnifiedMfaSection({ userId }: UnifiedMfaSectionProps) {
  const { t } = useTranslation('common');
  const [showMethodSelector, setShowMethodSelector] = useState(false);
  const [showTotpEnrollment, setShowTotpEnrollment] = useState(false);
  const [showEmailEnrollment, setShowEmailEnrollment] = useState(false);
  const [showSmsEnrollment, setShowSmsEnrollment] = useState(false);
  const [methodToRemove, setMethodToRemove] = useState<MfaMethod | null>(null);

  const { methods, hasAnyMfa, isLoading } = useUnifiedMFAStatus(userId);
  const unenrollMutation = useUnenrollMFAMethod();
  const updateOrderMutation = useUpdateMFAMethodOrder();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const getMethodLabel = (type: MfaMethodType) => {
    switch (type) {
      case 'totp':
        return t('security.mfa.authenticatorApp', 'Authenticator App');
      case 'email':
        return t('security.mfa.emailOtp', 'Email Verification');
      case 'sms':
        return t('security.mfa.smsOtp', 'SMS Verification');
    }
  };

  const handleMethodSelect = (method: SelectorMfaMethod) => {
    setShowMethodSelector(false);
    switch (method) {
      case 'totp':
        setShowTotpEnrollment(true);
        break;
      case 'email':
        setShowEmailEnrollment(true);
        break;
      case 'sms':
        setShowSmsEnrollment(true);
        break;
    }
  };

  const handleRemoveMethod = async () => {
    if (!methodToRemove) return;

    try {
      await unenrollMutation.mutateAsync({
        method: methodToRemove.type,
        factorId: methodToRemove.factorId,
      });
      // Clean removed method from stored order
      const currentOrder = methods.map(m => m.type);
      const newOrder = currentOrder.filter(t => t !== methodToRemove.type);
      if (newOrder.length > 0) {
        updateOrderMutation.mutate({ userId, methodOrder: newOrder });
      }
      setMethodToRemove(null);
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleEnrollmentSuccess = () => {
    setShowTotpEnrollment(false);
    setShowEmailEnrollment(false);
    setShowSmsEnrollment(false);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = methods.findIndex(m => m.type === active.id);
      const newIndex = methods.findIndex(m => m.type === over.id);
      const newOrder = arrayMove(methods, oldIndex, newIndex).map(m => m.type);
      updateOrderMutation.mutate({ userId, methodOrder: newOrder });
    }
  };

  // Get available methods for enrollment (exclude already enrolled)
  const getAvailableMethods = (): SelectorMfaMethod[] => {
    const enrolled = methods.map(m => m.type);
    const available: SelectorMfaMethod[] = [];
    if (!enrolled.includes('totp')) available.push('totp');
    if (!enrolled.includes('email')) available.push('email');
    if (!enrolled.includes('sms')) available.push('sms');
    return available;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {t('security.mfa.title', 'Two-Factor Authentication')}
          </CardTitle>
          <CardDescription>
            {t('security.mfa.description', 'Add extra security to your account with additional verification methods')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status indicator */}
          <div className="flex items-center gap-3 pb-4 border-b">
            {hasAnyMfa ? (
              <>
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{t('security.mfa.enabled', 'Enabled')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('security.mfa.methodsEnabled', '{{count}} verification method(s) active', { count: methods.length })}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <ShieldOff className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">{t('security.mfa.noMethods', 'No verification methods enabled')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('security.mfa.noMethodsDesc', 'Enable at least one method to secure your account')}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* Enrolled methods list with drag-and-drop */}
          {methods.length > 0 && (
            <div className="space-y-2">
              {methods.length > 1 && (
                <p className="text-xs text-muted-foreground">
                  {t('security.mfa.dragToReorder', 'Drag to reorder. The first method is used by default during login.')}
                </p>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={methods.map(m => m.type)} strategy={verticalListSortingStrategy}>
                  {methods.map((method, index) => (
                    <SortableMfaMethodCard
                      key={method.type}
                      method={method}
                      isPrimary={index === 0 && methods.length > 1}
                      showDragHandle={methods.length > 1}
                      getMethodLabel={getMethodLabel}
                      onRemove={setMethodToRemove}
                      isRemoving={unenrollMutation.isPending}
                      primaryLabel={t('security.mfa.primary', 'Primary')}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          )}

          {/* Add method button */}
          {getAvailableMethods().length > 0 && (
            <Button
              variant="outline"
              onClick={() => setShowMethodSelector(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('security.mfa.addMethod', 'Add verification method')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Method Selector Dialog */}
      <Dialog open={showMethodSelector} onOpenChange={setShowMethodSelector}>
        <DialogContent className="sm:max-w-md">
          <MfaMethodSelector
            availableMethods={getAvailableMethods()}
            onSelect={handleMethodSelect}
            onCancel={() => setShowMethodSelector(false)}
          />
        </DialogContent>
      </Dialog>

      {/* TOTP Enrollment Dialog */}
      <Dialog open={showTotpEnrollment} onOpenChange={setShowTotpEnrollment}>
        <DialogContent className="sm:max-w-lg">
          <MFAEnrollment
            onSuccess={handleEnrollmentSuccess}
            onCancel={() => setShowTotpEnrollment(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Email Enrollment Dialog */}
      <Dialog open={showEmailEnrollment} onOpenChange={setShowEmailEnrollment}>
        <DialogContent className="sm:max-w-md">
          <EmailMfaEnrollment
            onSuccess={handleEnrollmentSuccess}
            onCancel={() => setShowEmailEnrollment(false)}
          />
        </DialogContent>
      </Dialog>

      {/* SMS Enrollment Dialog */}
      <Dialog open={showSmsEnrollment} onOpenChange={setShowSmsEnrollment}>
        <DialogContent className="sm:max-w-md">
          <SmsMfaEnrollment
            onSuccess={handleEnrollmentSuccess}
            onCancel={() => setShowSmsEnrollment(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Remove Method Confirmation */}
      <AlertDialog open={!!methodToRemove} onOpenChange={(open) => !open && setMethodToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('security.mfa.removeConfirm.title', 'Remove verification method?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('security.mfa.removeConfirm.description',
                'This will remove {{method}} from your account. You can re-enable it at any time.',
                { method: methodToRemove ? getMethodLabel(methodToRemove.type) : '' }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMethod}
              disabled={unenrollMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {unenrollMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('security.mfa.removeConfirm.confirm', 'Remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
